import { Flex } from 'antd';
import 'bootstrap-icons/font/bootstrap-icons.min.css';
import React from 'react';

import styles from './tag.styles';

/**
 * Define la categoría de la etiqueta, que determina su esquema de color.
 */
export type TagCategory =
  | 'success'
  | 'warning'
  | 'error'
  | 'primary'
  | 'default'
  | 'transparent'
  | 'info';

/**
 * Props para el componente Tag.
 */
export type TagProps = {
  /**
   * Un título opcional para la etiqueta.
   */
  title?: React.ReactNode;
  /**
   * El contenido principal que se mostrará dentro de la etiqueta.
   */
  children: React.ReactNode;
  /**
   * La categoría de la etiqueta, que determina su color de fondo.
   *
   * @default 'default'
   */
  type?: TagCategory;
  /**
   * Un nombre de clase CSS opcional para aplicar al contenido de la etiqueta.
   */
  className?: string;
  /**
   * La posición del título en relación con los hijos.
   *
   * @default 'right'
   */
  titleSide?: 'left' | 'right';
  /**
   * El tamaño de la etiqueta, que afecta su relleno.
   *
   * @default 'small'
   */
  size?: 'small' | 'medium' | 'regular';
  /**
   * Si es verdadero, el contenido y el título se organizarán verticalmente.
   *
   * @default false
   */
  vertical?: boolean;
  /**
   * Estilos personalizados opcionales que se aplicarán al contenedor de la etiqueta.
   */
  style?: React.CSSProperties;
};

/**
 * Un componente de etiqueta flexible para mostrar información categorizada.
 *
 * @param {TagProps} props - Las props para el componente Tag.
 *
 * @returns {JSX.Element} El componente Tag renderizado.
 */
const Tag = ({
  title,
  children,
  type = 'default',
  className,
  titleSide = 'right',
  size = 'small',
  vertical = false,
  style
}: TagProps) => {
  const delegatedStyles = {
    ...style
  };

  return (
    <Flex
      style={{
        ...styles.box,
        backgroundColor: styles.background[type].backgroundColor,
        padding: styles.size[size].padding,
        ...delegatedStyles
      }}>
      <Flex vertical={vertical} className={className} gap={0}>
        {title && titleSide === 'left' ? (
          <span className='body-small font-light'>{title}</span>
        ) : (
          ''
        )}
        {children}
        {title && titleSide === 'right' ? <span>{title}</span> : ''}
      </Flex>
    </Flex>
  );
};

export default Tag;
